import React from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, Lock, User, Shield, Crown, Key } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { usePermissionContext } from "@/context/PermissionContext";
import { ROLE_LEVELS } from "@/types/permissions";

/**
 * Component to display when user lacks permission
 */
interface PermissionDeniedProps {
  resource?: string;
  action?: string;
  page?: string;
  minimumRole?: string;
  className?: string;
}

export const PermissionDenied: React.FC<PermissionDeniedProps> = ({
  resource,
  action,
  page,
  minimumRole,
  className = "",
}) => {
  const { getRoleLevel } = usePermissionContext();
  const currentLevel = getRoleLevel();
  const navigate = useNavigate();

  const getRoleName = (level: number) => {
    switch (level) {
      case ROLE_LEVELS.EXTERNAL:
        return "External";
      case ROLE_LEVELS.CONTRIBUTOR:
        return "Contributor";
      case ROLE_LEVELS.PROJECT_MANAGER:
        return "Project Manager";
      case ROLE_LEVELS.ADMIN:
        return "Admin";
      default:
        return "Unknown";
    }
  };

  const getRoleIcon = (level: number) => {
    switch (level) {
      case ROLE_LEVELS.EXTERNAL:
        return <User className="h-4 w-4" />;
      case ROLE_LEVELS.CONTRIBUTOR:
        return <Key className="h-4 w-4" />;
      case ROLE_LEVELS.PROJECT_MANAGER:
        return <Shield className="h-4 w-4" />;
      case ROLE_LEVELS.ADMIN:
        return <Crown className="h-4 w-4" />;
      default:
        return <Lock className="h-4 w-4" />;
    }
  };

  return (
    <div className="flex items-center h-full">
      <Card className={`max-w-md mx-auto ${className}`}>
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-red-100 rounded-full">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold text-gray-900">Access Denied</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600 text-center">
            You don't have sufficient permissions to access this {page ? "page" : "resource"}.
          </p>

          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Your Current Role:</span>
              <Badge variant="secondary" className="flex items-center gap-1">
                {getRoleIcon(currentLevel)}
                {getRoleName(currentLevel)}
              </Badge>
            </div>

            {resource && action && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Required for:</div>
                <div className="text-sm text-gray-600">
                  <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">
                    {resource}.{action}
                  </code>
                </div>
              </div>
            )}

            {page && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Page:</div>
                <div className="text-sm text-gray-600">
                  <code className="bg-gray-200 px-1 py-0.5 rounded text-xs">{page}</code>
                </div>
              </div>
            )}

            {minimumRole && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-medium text-gray-700 mb-1">Minimum Required Role:</div>
                <div className="text-sm text-gray-600">{minimumRole}</div>
              </div>
            )}
          </div>

          <div className="pt-4">
            <Button variant="outline" className="w-full" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            Contact your administrator if you believe you should have access to this resource.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Simple permission denied message for inline use
 */
export const InlinePermissionDenied: React.FC<{ message?: string }> = ({
  message = "You don't have permission to perform this action.",
}) => (
  <div className="flex items-center gap-2 p-2 text-sm text-gray-600 bg-gray-50 rounded border-l-4 border-red-400">
    <Lock className="h-4 w-4 text-red-500" />
    <span>{message}</span>
  </div>
);
